import { useState, useContext, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Plus, FileSpreadsheet, RefreshCw, ChevronDown } from "lucide-react";
import { useResourceManagement } from "./hooks/useResourceManagement";
import { ProjectContext } from "./ProjectStructureTab";
import ResourceDialog from "./resource-management/ResourceDialog";
import ResourceList from "./resource-management/ResourceList";
import ResourceFlowChart from "./resource-management/ResourceFlowChart";
import ResourceAssignmentDialog from "./resource-management/ResourceAssignmentDialog";
import ImportResourcesDialog from "./resource-management/ImportResourcesDialog";
import ConfirmationDialog from "./resource-management/ConfirmationDialog";

const ResourceManagementTab = () => {
  const {
    selectedProject
  } = useContext(ProjectContext);
  const {
    resources,
    resourceAssignments,
    isLoading,
    isAddResourceDialogOpen,
    setIsAddResourceDialogOpen,
    isEditResourceDialogOpen,
    setIsEditResourceDialogOpen,
    selectedResource,
    setSelectedResource,
    isAddAssignmentDialogOpen,
    setIsAddAssignmentDialogOpen,
    isEditAssignmentDialogOpen,
    setIsEditAssignmentDialogOpen,
    selectedAssignment,
    setSelectedAssignment,
    addResource,
    updateResource,
    deleteResource,
    addResourceAssignment,
    updateResourceAssignment,
    deleteResourceAssignment,
    refreshResources,
    canViewResourcePool,
    canEditResource,
    canImportResource,
    userRole
  } = useResourceManagement(selectedProject?.id || null, selectedProject?.name || null);
  const isActionDisabled = userRole === 'Manager' && !canEditResource;
  const isImportDisabled = (userRole === 'Manager' || userRole === 'Director') && !canImportResource;

  const [tasks, setTasks] = useState<any[]>([]);
  const [confirmDeleteResource, setConfirmDeleteResource] = useState<string | null>(null);
  const [confirmDeleteAssignment, setConfirmDeleteAssignment] = useState<string | null>(null);
  const [isImportResourcesOpen, setIsImportResourcesOpen] = useState(false);
  const [isOrgChartOpen, setIsOrgChartOpen] = useState(false);

  useEffect(() => {
    // Load tasks for the selected project
    if (selectedProject && selectedProject.tasks) {
      setTasks(selectedProject.tasks);
      // NOTE: Project task record creation is now handled exclusively by ResourceAssignmentTab
      // to prevent duplicate record creation and race conditions
      console.log('ResourceManagementTab: Tasks loaded, project task records will be created by ResourceAssignmentTab');
    }
  }, [selectedProject]);

  const handleAddResource = async (resourceData: any) => {
    const success = await addResource(resourceData);
    return success;
  };

  const handleUpdateResource = async (resourceData: any) => {
    if (!selectedResource) return false;
    const success = await updateResource(selectedResource.id, resourceData);
    return success;
  };

  const handleAddAssignment = async (assignmentData: any) => {
    const success = await addResourceAssignment(assignmentData);
    return success;
  };

  const handleUpdateAssignment = async (assignmentData: any) => {
    if (!selectedAssignment) return false;
    const success = await updateResourceAssignment(selectedAssignment.id, assignmentData);
    return success;
  };

  const handleConfirmDeleteResource = async (resourceId: string) => {
    const success = await deleteResource(resourceId);
    setConfirmDeleteResource(null);
    return success;
  };

  const handleConfirmDeleteAssignment = async (assignmentId: string) => {
    const success = await deleteResourceAssignment(assignmentId);
    setConfirmDeleteAssignment(null);
    return success;
  };

  // Format and display project duration
  const formatDuration = (days?: number) => {
    if (!days && days !== 0) return "Not specified";
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  return (
    <div className="grid grid-cols-12 gap-2 my-0 py-0">
      {/* Project Summary Section */}
      {selectedProject && (
        <div className="col-span-12 mb-0">
          <Card className="bg-secondary/50 backdrop-blur-lg border-primary rounded-lg shadow-lg shadow-purple-500/10 py-1">
            <CardContent className="pt-0 py-0 px-6">
              <div className="flex items-center gap-8 text-white">
                <div>
                  <span className="text-[13px] text-gray-400">Project: </span>
                  <span className="text-[12px] text-white">{selectedProject.name}</span>
                </div>
                <div>
                  <span className="text-[13px] text-gray-400">Start Date: </span>
                  <span className="text-[12px] text-white">
                    {selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : "Not set"}
                  </span>
                </div>
                <div>
                  <span className="text-[13px] text-gray-400">End Date: </span>
                  <span className="text-[12px] text-white">
                    {selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : "Not set"}
                  </span>
                </div>
                <div>
                  <span className="text-[13px] text-gray-400">Duration: </span>
                  <span className="text-[12px] text-white">{formatDuration(selectedProject.duration)}</span>
                </div>
                <div>
                  <span className="text-[13px] text-gray-400">Tasks: </span>
                  <span className="text-[12px] text-white">{selectedProject.tasks?.length || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Resource Pool Section - Keep existing height constraint */}
      {canViewResourcePool && (
      <div className="col-span-12">
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 h-[450px] py-0 my-[5px]">
          <CardHeader className="pb-1 py-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-white text-[20px]">Resource Pool</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={isImportDisabled} className={`border-primary/20 ${isImportDisabled ? 'bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600' : 'bg-primary hover:bg-primary/90 text-black'}`} onClick={() => setIsImportResourcesOpen(true)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Import
                </Button>

                <Button variant="outline" size="sm" className="border-primary/20 bg-primary hover:bg-primary/90 text-black" onClick={refreshResources}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" disabled={isActionDisabled} className={`border-primary/20 ${isActionDisabled ? 'bg-gray-600 text-gray-400 cursor-not-allowed hover:bg-gray-600' : 'bg-primary hover:bg-primary/90 text-black'}`} onClick={() => setIsAddResourceDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-[400px]">
            <ResourceList resources={resources} isLoading={isLoading} selectedResourceId={selectedResource?.id} onSelectResource={setSelectedResource} onEditResource={() => setIsEditResourceDialogOpen(true)} onDeleteResource={setConfirmDeleteResource} onAddResource={() => setIsAddResourceDialogOpen(true)} isDraggable={true} onImportSuccess={refreshResources} isActionDisabled={isActionDisabled} />
          </CardContent>
        </Card>
      </div>
      )}
      
      {/* Resource Flowchart - Remove height constraint when expanded */}
      <div className="col-span-12">
        <Collapsible open={isOrgChartOpen} onOpenChange={setIsOrgChartOpen}>
          <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10">
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-1 my-0 py-[2px] cursor-pointer hover:bg-primary/10 transition-colors">
                <div className="flex justify-between items-center w-full">
                  <div className="flex flex-col items-start">
                    <CardTitle className="text-white text-[20px]">Visualization of unallocated resources</CardTitle>
                    <span className="text-gray-400 text-[12px] font-normal">(Click on nodes to view resource details)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">(Click to expand or collapse)</span>
                    <ChevronDown className={`h-4 w-4 text-primary-light transition-transform duration-200 ${isOrgChartOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-0">
                <div className={isOrgChartOpen ? "min-h-[500px]" : "h-0"}>
                  <ResourceFlowChart resources={resources} assignments={resourceAssignments} tasks={tasks} />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
      
      {/* Dialogs */}
      <ResourceDialog open={isAddResourceDialogOpen} onOpenChange={setIsAddResourceDialogOpen} onSave={handleAddResource} />
      
      <ResourceDialog open={isEditResourceDialogOpen} onOpenChange={setIsEditResourceDialogOpen} onSave={handleUpdateResource} resource={selectedResource} isEdit />
      
      <ResourceAssignmentDialog open={isAddAssignmentDialogOpen} onOpenChange={setIsAddAssignmentDialogOpen} onSave={handleAddAssignment} resources={resources} tasks={tasks} projectId={selectedProject?.id || null} />
      
      <ResourceAssignmentDialog open={isEditAssignmentDialogOpen} onOpenChange={setIsEditAssignmentDialogOpen} onSave={handleUpdateAssignment} resources={resources} assignment={selectedAssignment} tasks={tasks} isEdit projectId={selectedProject?.id || null} />
      
      <ConfirmationDialog open={!!confirmDeleteResource} onOpenChange={() => setConfirmDeleteResource(null)} onConfirm={() => confirmDeleteResource && handleConfirmDeleteResource(confirmDeleteResource)} title="Delete Resource" description="Are you sure you want to delete this resource? This action cannot be undone." />
      
      <ConfirmationDialog open={!!confirmDeleteAssignment} onOpenChange={() => setConfirmDeleteAssignment(null)} onConfirm={() => confirmDeleteAssignment && handleConfirmDeleteAssignment(confirmDeleteAssignment)} title="Delete Assignment" description="Are you sure you want to remove this resource assignment? This action cannot be undone." />
      
      <ImportResourcesDialog 
        open={isImportResourcesOpen} 
        onOpenChange={setIsImportResourcesOpen} 
        projectId={selectedProject?.id} 
        projectName={selectedProject?.name}
        onImportSuccess={refreshResources} 
      />
    </div>
  );
};

export default ResourceManagementTab;
